import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function StrategySection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const strategies = [
    {
      id: "hk",
      titleKey: "strategy.hk.title",
      subtitleKey: "strategy.hk.subtitle",
      descKey: "strategy.hk.desc",
      pointsKeys: ["strategy.hk.points.0", "strategy.hk.points.1", "strategy.hk.points.2"],
      bgClass: "bg-ax-dark",
      textClass: "text-white",
      img: "https://readdy.ai/api/search-image?query=Modern%20Hong%20Kong%20tech%20innovation%20hub%20interior%20with%20clean%20laboratory%20and%20research%20workstations%2C%20warm%20ambient%20lighting%2C%20sleek%20minimalist%20design%2C%20semiconductor%20research%20equipment%2C%20professional%20photography%20style%2C%20soft%20warm%20color%20tones&width=600&height=400&seq=ax-strategy-hk&orientation=landscape",
      tagKey: "strategy.hk.tag",
      iconType: "hk" as const,
    },
    {
      id: "cn",
      titleKey: "strategy.cn.title",
      subtitleKey: "strategy.cn.subtitle",
      descKey: "strategy.cn.desc",
      pointsKeys: ["strategy.cn.points.0", "strategy.cn.points.1", "strategy.cn.points.2"],
      bgClass: "bg-ax-light-warm",
      textClass: "text-ax-dark",
      img: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20modern%20Shenzhen%20or%20Guangzhou%20industrial%20park%20with%20high-tech%20manufacturing%20facilities%2C%20clean%20organized%20factory%20buildings%2C%20warm%20golden%20hour%20lighting%2C%20cinematic%20wide%20angle%20photography%2C%20modern%20Chinese%20tech%20hub%20landscape&width=600&height=400&seq=ax-strategy-1&orientation=landscape",
      tagKey: "strategy.cn.tag",
      iconType: "cn" as const,
    },
    {
      id: "global",
      titleKey: "strategy.global.title",
      subtitleKey: "strategy.global.subtitle",
      descKey: "strategy.global.desc",
      pointsKeys: ["strategy.global.points.0", "strategy.global.points.1", "strategy.global.points.2"],
      bgClass: "bg-white border border-ax-silver-light",
      textClass: "text-ax-dark",
      img: "https://readdy.ai/api/search-image?query=Abstract%20global%20logistics%20network%20visualization%20with%20connected%20world%20map%20nodes%2C%20warm%20golden%20and%20amber%20lighting%2C%20shipping%20containers%20and%20data%20flow%20streams%2C%20modern%20digital%20art%20style%2C%20soft%20warm%20tones%2C%20professional%20commercial%20photography&width=600&height=400&seq=ax-strategy-global&orientation=landscape",
      tagKey: "strategy.global.tag",
      iconType: "global" as const,
    },
  ];

  return (
    <section
      id="strategy"
      ref={sectionRef}
      className="bg-white py-20 md:py-28"
    >
      <div className="px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="text-center mb-16 animate-in opacity-0 translate-y-6 transition-all duration-700">
          <span className="text-xs text-ax-silver-dark tracking-[0.3em] uppercase block mb-4">
            {t("strategy.label")}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ax-dark font-bold">
            {t("strategy.title")}
          </h2>
          <p className="text-ax-dark/50 text-sm md:text-base mt-4 max-w-2xl mx-auto">
            {t("strategy.subtitle")}
          </p>
        </div>

        {/* Strategy Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {strategies.map((s, i) => (
            <div
              key={s.id}
              className={`animate-in opacity-0 translate-y-6 transition-all duration-700 rounded-2xl overflow-hidden ${s.bgClass}`}
              style={{ transitionDelay: `${200 + i * 200}ms` }}
            >
              {s.img && (
                <div className="relative">
                  <img
                    src={s.img}
                    alt={t(s.titleKey)}
                    className="w-full h-[200px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ax-dark/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="inline-flex items-center gap-2 bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                      {s.iconType === "hk" && <span className="w-4 h-4 flex items-center justify-center text-[10px] font-bold">HK</span>}
                      {s.iconType === "cn" && <i className="ri-building-2-line" />}
                      {s.iconType === "global" && <i className="ri-global-line" />}
                      {t(s.tagKey)}
                    </span>
                  </div>
                </div>
              )}

              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  {s.iconType === "hk" && (
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">HK</span>
                    </div>
                  )}
                  {s.iconType === "cn" && (
                    <div className="w-10 h-10 bg-ax-red/10 rounded-full flex items-center justify-center">
                      <i className="ri-building-2-line text-ax-red" />
                    </div>
                  )}
                  {s.iconType === "global" && (
                    <div className="w-10 h-10 bg-ax-dark/10 rounded-full flex items-center justify-center">
                      <i className="ri-global-line text-ax-dark" />
                    </div>
                  )}
                  <div>
                    <h3 className={`font-serif text-xl md:text-2xl font-bold ${s.textClass}`}>
                      {t(s.titleKey)}
                    </h3>
                    <p className={`text-xs ${s.id === "hk" ? "text-white/50" : "text-ax-silver-dark"}`}>
                      {t(s.subtitleKey)}
                    </p>
                  </div>
                </div>

                <p className={`text-sm leading-relaxed mb-5 ${s.id === "hk" ? "text-white/70" : "text-ax-dark/60"}`}>
                  {t(s.descKey)}
                </p>

                <div className="space-y-2">
                  {s.pointsKeys.map((p) => (
                    <div key={p} className="flex items-center gap-2">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${s.id === "hk" ? "bg-ax-red" : "bg-ax-red"}`}
                      />
                      <span
                        className={`text-xs ${s.id === "hk" ? "text-white/60" : "text-ax-dark/50"}`}
                      >
                        {t(p)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}